import { getDictionaryLabel, type Dictionary } from "@/domain/dictionary";
import { getSupabase, unwrap } from "./supabaseClient";

export interface CustomField {
  id: string;
  location: string | null;
  field_key: string;
  label_no: string;
  label_en: string;
  field_type: "text" | "textarea";
  sort_order: number;
  admin_only: boolean;
  /** CSS class name (historically named css in the database). */
  css: string;
}

export type CustomFieldInput = Omit<CustomField, "id">;
export type CustomValues = Record<string, string>;

export function fieldsForLocation(fields: CustomField[], location: string): CustomField[] {
  return fields
    .filter((field) => field.location === null || field.location === location)
    .sort((a, b) => a.sort_order - b.sort_order || a.field_key.localeCompare(b.field_key));
}

/** Class names only. Never interpret administrator input as CSS declarations. */
export function validCustomCss(value: string): boolean {
  return value === "" || /^[a-z][a-z0-9_-]{0,63}$/i.test(value);
}

export function customFieldClassName(field: CustomField): string {
  return validCustomCss(field.css) ? field.css : "";
}

/** Resolve language translations using the existing configuration dictionary. */
export function customFieldLabel(
  field: CustomField,
  language: string,
  dictionary: Dictionary | null,
): string {
  // The English source always comes from the field definition, not an older
  // dictionary snapshot that may predate a field rename.
  if (language === "en") return field.label_en;

  // Previously the field editor allowed a Norwegian label. Respect those
  // existing values until an administrator replaces them in Translations.
  const norwegian = field.label_no !== field.label_en ? field.label_no : "";
  return getDictionaryLabel(
    dictionary ?? {},
    "configuration",
    `customField:${field.id}`,
    { en: field.label_en, no: norwegian },
    language,
  );
}

export async function fetchCustomFields(): Promise<CustomField[]> {
  return unwrap(
    await getSupabase()
      .from("intervention_custom_fields")
      .select("id, location, field_key, label_no, label_en, field_type, sort_order, admin_only, css")
      .order("sort_order")
      .order("field_key"),
  ) as CustomField[];
}

export async function saveCustomField(field: CustomFieldInput, id?: string): Promise<void> {
  if (id) {
    unwrap(await getSupabase().from("intervention_custom_fields").update(field).eq("id", id));
  } else {
    unwrap(await getSupabase().from("intervention_custom_fields").insert(field));
  }
}

export async function deleteCustomField(id: string): Promise<void> {
  unwrap(await getSupabase().from("intervention_custom_fields").delete().eq("id", id));
}

export async function fetchCustomValues(interventionId: string): Promise<CustomValues> {
  const rows = unwrap(
    await getSupabase()
      .from("intervention_custom_values")
      .select("field_id, value")
      .eq("intervention_id", interventionId),
  ) as { field_id: string; value: string }[];
  return Object.fromEntries(rows.map((row) => [row.field_id, row.value]));
}

export async function saveCustomValues(
  interventionId: string,
  fields: CustomField[],
  values: CustomValues,
): Promise<void> {
  const entries = fields.map((field) => ({
    intervention_id: interventionId,
    field_id: field.id,
    value: (values[field.id] ?? "").trim(),
  }));
  const populated = entries.filter((entry) => entry.value);
  const cleared = entries.filter((entry) => !entry.value);
  if (populated.length) {
    unwrap(
      await getSupabase()
        .from("intervention_custom_values")
        .upsert(populated, { onConflict: "intervention_id,field_id" }),
    );
  }
  if (cleared.length) {
    unwrap(
      await getSupabase()
        .from("intervention_custom_values")
        .delete()
        .eq("intervention_id", interventionId)
        .in(
          "field_id",
          cleared.map((entry) => entry.field_id),
        ),
    );
  }
}
